/**
 * `public_profile` comes back from the API as a real boolean, but some older
 * cached/local state stored it as the string 'true'/'false' — handle both.
 */
export function isPublicProfileEnabled(
  member: { public_profile?: boolean | string } | null | undefined
): boolean {
  const p = member?.public_profile
  if (p === true) return true
  if (typeof p === 'string') return p.toLowerCase() === 'true'
  return false
}

/** Route id used to build a `/p/:route-id` public profile link. */
export function publicProfileRouteId(
  member: { slug_id?: string; created_at?: string } | null | undefined
): string | undefined {
  return member?.slug_id ?? member?.created_at
}

/**
 * Chat identity for a corp member — chats key participants by slug_id, falling back to a
 * stringified numeric id only for the rare case a member has none (predates the slug_id
 * backfill, or a cached profile is stale).
 */
export function chatIdentityOf(
  member: { id?: number; slug_id?: string } | null | undefined
): string | undefined {
  return member?.slug_id ?? (member?.id !== undefined ? String(member.id) : undefined)
}

/**
 * What chat shows as someone's name: their first name, else their nickname, else their state code
 * (masked - see `maskStateCode`).
 * `||`, not `??`: a blank first name (email sign-ups don't ask for one) falls through too.
 */
export function displayNameOf(
  member: { first_name?: string | null; nickname?: string | null; state_code?: string | null } | null | undefined
): string {
  return member?.first_name?.trim() || member?.nickname?.trim() || maskStateCode(member?.state_code)
}

/**
 * A state code as chat shows it, so a member without a name isn't identified by it:
 * every digit of the closing number but the last becomes `*` - `LA/25C/0421` -> `LA/25C/***1`.
 */
export function maskStateCode(stateCode: string | null | undefined): string {
  return (stateCode ?? '').replace(/\d(?=\d+$)/g, '*')
}

/*
 * State code format: `<state>/<yy><stream>/<serial>`, e.g. `LA/24A/12345`.
 * Single source of truth for state code validation in the frontend — change
 * these pieces rather than writing a new regex elsewhere. Keep in sync with
 * the backend's `utilities/ngstates.js`.
 */
const STATE_CODE_STATES = 'ab|ad|ak|an|ba|by|bn|bo|cr|dt|eb|ed|ek|en|fc|gm|im|jg|kd|kn|kt|kb|kg|kw|la|ns|ng|og|od|os|oy|pl|rv|so|tr|yb|zm'
const STATE_CODE_YEAR = '\\d\\d'
const STATE_CODE_STREAMS = '[abc]'
const STATE_CODE_SERIAL = '[0-9]{4,5}'

export const STATE_CODE_REGEX = new RegExp(`^(${STATE_CODE_STATES})\\/${STATE_CODE_YEAR}${STATE_CODE_STREAMS}\\/${STATE_CODE_SERIAL}$`, 'i')
